/* eslint-disable @typescript-eslint/indent */

import { ComponentPropsWithoutRef } from 'react';
import clsx from 'clsx';

import { Container } from './Container';
import { FadeIn } from './FadeIn';

/**
 * Configuration properties for the SectionIntro component.
 */
type TSectionIntroProps = Omit<
  ComponentPropsWithoutRef<typeof Container>,
  'title' | 'children'
> & {
  /** The main title of the section. */
  title: string;
  /** Small eyebrow text above the section title. */
  eyebrow?: string;
  /** Additional descriptive content. */
  children?: React.ReactNode;
  /** Whether the title should use a smaller font size. */
  smaller?: boolean;
  /** Whether to invert colors for dark backgrounds. */
  invert?: boolean;
};

/**
 * A component for introducing a new section of a page with a title and eyebrow.
 */
export function SectionIntro({
  title,
  eyebrow,
  children,
  smaller = false,
  invert = false,
  ...props
}: TSectionIntroProps) {
  return (
    <Container {...props}>
      <FadeIn className="max-w-2xl">
        <h2>
          {eyebrow && (
            <>
              <span
                className={clsx(
                  'mb-6 block text-base font-semibold',
                  invert ? 'text-white' : 'text-gray-950'
                )}
              >
                {eyebrow}
              </span>
              <span className="sr-only"> - </span>
            </>
          )}
          <span
            className={clsx(
              'block tracking-tight [text-wrap:balance]',
              smaller
                ? 'text-2xl font-semibold'
                : 'text-4xl font-medium sm:text-5xl',
              invert ? 'text-white' : 'text-gray-950'
            )}
          >
            {title}
          </span>
        </h2>
        {children && (
          <div
            className={clsx(
              'mt-6 text-xl',
              invert ? 'text-gray-300' : 'text-gray-600'
            )}
          >
            {children}
          </div>
        )}
      </FadeIn>
    </Container>
  );
}
